import React, { useEffect, useRef, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import moment from "moment-timezone"
import { familyName, supplierNames } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { BALLS_TBD } from "pages/EventsManagement/eventCreationForm"
import { packageContent, playerPackageDraft } from "pages/EventsManagement/eventPlayerPackage"
import { TEXT_BLOCKS, descriptionDraft } from "pages/EventsManagement/eventTexts"
import LocalizedTextBlock from "components/Common/LocalizedTextBlock"
import { countryLanguage } from "components/Common/localizedText"
import { PASSED_PHASES, categoryLabel, clubLabel, competitionTile, daysLabel, playModeLabel, sportLabel } from "./plannerLabels"
import { valueSource } from "./proposalHistory"
import { SourcePill } from "./ProposalEventFields"
import { FIELD_NAMES, completeness, problemLabel, problemSubject } from "./publishingLabels"

// The publish review (wayfinder ticket 18; E2p pinned decisions Q1–Q2): one page for the proposals of a batch, each as publishing would
// create it (Phase E1p's preview). What the organizer confirmed is read-only, changed through "Edit proposal" (a change returns it to
// Proposed and it leaves the batch); every other value is filled or changed here and saved at once as an edit of the proposal, so nothing
// is lost if the review is closed. Each value shows its source.

const ENTRIES = [1, 2, 3]
const DRAW_MODELS = [{ key: "eliminatory", name: "Eliminatory" }, { key: "groups", name: "Groups" }]
// "Mon 2 Nov, 20:00": an instant of a phase, in the country's time zone.
const instant = (value, timeZone) => moment(value).tz(timeZone).format("ddd D MMM, HH:mm")
const phaseLine = (label, phase, timeZone) => `${label}: ${instant(phase.startDate, timeZone)} – ${instant(phase.endDate, timeZone)}`

// A value as its control shows it, and the change an edit sends for what the control holds.
const empty = value => value === null || value === undefined || value === ""
const shown = (field, value) => {
  if (empty(value)) return ""
  if (field === "officialBall") return value.tbd ? BALLS_TBD : value.name
  if (field === "coordinator") return value.name
  return String(value)
}
const stored = (field, input) => {
  const text = String(input).trim()
  if (!text) return null
  if (field === "officialBall") return input === BALLS_TBD ? { tbd: true } : { name: input }
  if (field === "coordinator") return { name: input }
  if (["numberOfCourts", "singlesMaxNumber", "doublesMaxNumber"].includes(field)) return Number(text)
  return text
}

// A line of text saved when it is left or Enter is pressed, if it changed.
function InlineText({ id, value, disabled, onSave, type = "text", invalid }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const save = () => { if (draft.trim() !== value.trim()) onSave(draft) }
  return <input id={id} className="form-control form-control-sm" type={type} value={draft} disabled={disabled} aria-invalid={invalid || undefined}
    min={type === "number" ? 0 : undefined} onChange={event => setDraft(event.target.value)} onBlur={save}
    onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); save() } }} />
}

// A text block of the event (ADR 0013) written in the review: its original, in its language, saved as one edit with its button. A
// proposal without the text starts it in its country's language.
const SAVE_TEXT = { description: "Save the description", playerPackage: "Save the player package" }
function ReviewText({ id, name, value, country, source, disabled, onSave }) {
  const block = TEXT_BLOCKS[name]
  const language = countryLanguage(country)
  const saved = JSON.stringify(value || (name === "description" ? descriptionDraft(language, "") : playerPackageDraft(language, packageContent(), false)))
  const [draft, setDraft] = useState(() => JSON.parse(saved))
  useEffect(() => setDraft(JSON.parse(saved)), [saved])
  return <LocalizedTextBlock id={id} open={false} title={<>{block.title}{source && <span className="ep-source" title={source.title}>{source.label}</span>}</>}
    fields={block.fields} original={block.original(draft)} onLanguage={next => setDraft(block.declare(draft, next))}
    onContent={content => setDraft(name === "playerPackage" ? { ...block.write(draft, content), tShirt: draft.tShirt } : block.write(draft, content))}>
    {name === "playerPackage" && <label className="ep-check"><input type="checkbox" checked={Boolean(draft.tShirt)} disabled={disabled}
      onChange={() => setDraft({ ...draft, tShirt: !draft.tShirt })} />T-shirt included</label>}
    <button type="button" className="btn btn-light btn-sm" disabled={disabled || JSON.stringify(draft) === saved} onClick={() => onSave(draft)}>{SAVE_TEXT[name]}</button>
  </LocalizedTextBlock>
}

// What stops a value (a problem of Ready to publish) shows instead of its source: "Missing", or "To fix" for one the settings or the
// catalogue no longer take.
const flagOf = problems => (!problems.length ? null : problems.every(({ reason }) => reason === "missing") ? "Missing" : "To fix")
// `plain` for a value with no control: its name is not a label.
function ReviewField({ id, label, source, flag, info, plain, children }) {
  return <div className={"ep-review-field" + (flag ? " is-missing" : "")}>
    {plain ? <span className="ep-review-label">{label}</span> : <label htmlFor={id}>{label}</label>}
    <span className="ep-stack">{children}{info && <span className="ep-meta">{info}</span>}</span>
    <span>{flag ? <span className="ep-source is-missing">{flag}</span>
      : source && <span className={"ep-source" + (source.kind === "edited" ? " is-edited" : "")} title={source.title}>{source.label}</span>}</span>
  </div>
}

// `onSave(changes, what)` sends an edit of the proposal; its changes are built on the proposal as stored (`record`), so a value the fill
// rule gives is never stored by an edit of another one. `focusField` is a problem's path, whose control takes the focus.
export default function PublishReview({ row, record, settings, timeZone, saving, onSave, onEditProposal, focusField }) {
  const context = { proposal: { sources: row.sources }, history: record.history || [], timeZone }
  const locked = saving || !record.proposal
  // The field a problem links to takes the focus once, as soon as it can be changed.
  const focused = useRef(null)
  useEffect(() => {
    const key = `${row.id}|${focusField}`
    const control = focusField && document.getElementById(`review-${row.id}-${focusField}`)
    if (!control || control.disabled || focused.current === key) return
    control.focus()
    focused.current = key
  }, [row.id, focusField, locked])
  const sourceOf = path => valueSource(context, path)
  const problemOf = field => row.problems.find(problem => problem.field === field)
  const id = field => `review-${row.id}-${field}`
  const options = (list, emptyLabel) => [<option key="" value="">{emptyLabel}</option>,
    ...(list || []).map(item => <option key={String(item.key ?? item.name)} value={String(item.key ?? item.name)}>{item.name ?? String(item.key)}</option>)]
  const select = (field, list) => <select id={id(field)} className="form-control form-control-sm" value={shown(field, row.values[field])} disabled={locked}
    aria-invalid={Boolean(problemOf(field)) || undefined} onChange={event => onSave({ [field]: stored(field, event.target.value) }, FIELD_NAMES[field])}>
    {options(list, "Not chosen yet")}</select>
  const text = (field, type) => <InlineText id={id(field)} type={type} value={shown(field, row.values[field])} disabled={locked}
    invalid={Boolean(problemOf(field))} onSave={input => onSave({ [field]: stored(field, input) }, FIELD_NAMES[field])} />
  // A competition's value: the stored competitions, only that one's value changed.
  const saveCompetition = (competitionId, key, value) => onSave({ competitions: record.proposal.competitions.map(competition => (competition.id === competitionId
    ? { ...competition, [key]: value } : competition)) }, problemSubject(`competitions.${competitionId}.${key}`, row.competitions))
  const competitionControl = (competition, key, list) => {
    const path = `competitions.${competition.id}.${key}`
    const label = problemSubject(path, row.competitions)
    const value = competition[key] === null || competition[key] === undefined ? "" : String(competition[key])
    const number = ["level", "numberOfGroups", "mlMaxNumber"].includes(key)
    return <span className={"ep-field" + (problemOf(path) ? " is-missing" : "")} key={key}>
      <span className="ep-field-head"><label htmlFor={id(path)}>{label}</label><SourcePill source={sourceOf(path)} /></span>
      {list ? <select id={id(path)} className="form-control form-control-sm" value={value} disabled={locked} aria-invalid={Boolean(problemOf(path)) || undefined}
        onChange={event => saveCompetition(competition.id, key, event.target.value === "" ? null : number ? Number(event.target.value) : event.target.value)}>
        {options(list, "Not chosen yet")}</select>
        : <InlineText id={id(path)} type="number" value={value} disabled={locked} invalid={Boolean(problemOf(path))}
          onSave={input => saveCompetition(competition.id, key, input.trim() === "" ? null : Number(input))} />}
    </span>
  }
  const field = (name, control, info, plain) => <ReviewField key={name} id={id(name)} label={FIELD_NAMES[name]} source={sourceOf(name)} plain={plain}
    flag={flagOf(problemOf(name) ? [problemOf(name)] : [])}
    info={info || (problemOf(name) && problemOf(name).reason !== "missing" ? problemLabel(problemOf(name)) : null)}>{control}</ReviewField>
  const balls = [...(settings.balls || []).map(ball => ({ key: ball.name, name: ball.name })),
    ...(row.values.officialBall?.name && !(settings.balls || []).some(ball => ball.name === row.values.officialBall.name) ? [{ key: row.values.officialBall.name, name: row.values.officialBall.name }] : []),
    { key: BALLS_TBD, name: "Balls TBD" }]
  const coordinators = [...(settings.coordinators || []).map(({ name }) => ({ key: name, name })),
    ...(row.values.coordinator?.name && !(settings.coordinators || []).some(({ name }) => name === row.values.coordinator.name) ? [{ key: row.values.coordinator.name, name: row.values.coordinator.name }] : [])]
  const entries = ENTRIES.map(key => ({ key, name: String(key) }))
  const place = row.club?.kind === "club" ? row.club.city || row.club.clubName : clubLabel(row.club)

  return <section className="ep-card ep-stack ep-gap ep-review" aria-label={`${row.number} · ${categoryLabel(settings, row.category)} · ${place}`}>
    <header className="ep-panel-head">
      <div className="ep-stack">
        <h2>{`${row.number} · ${categoryLabel(settings, row.category)} · ${place}`}</h2>
        <span className="ep-meta">{`${row.name || "No name yet"} · ${row.organizer?.name || "No organizer"}`}</span>
      </div>
      <span className="ep-spacer" />
      <span className={`ep-mark ep-mark-${completeness(row).tone}`}>{completeness(row).label}</span>
      <button type="button" className="btn btn-light btn-sm" onClick={() => onEditProposal(row)}>Edit proposal</button>
    </header>
    {record.error && <Alert color="danger" className="mb-0">{record.error}</Alert>}

    <div role="group" aria-label="Confirmed by the organizer" className="ep-stack ep-gap">
      <h3>Confirmed by the organizer</h3>
      <dl className="ep-facts">
        <dt>Dates</dt><dd>{daysLabel(row.days)}</dd>
        <dt>Club</dt><dd>{clubLabel(row.club)}</dd>
        <dt>Organizer</dt><dd>{row.organizer?.name || "No organizer"}</dd>
        <dt>Category</dt><dd>{categoryLabel(settings, row.category)}</dd>
        <dt>Play mode</dt><dd>{playModeLabel(row.playMode) || "—"}</dd>
        <dt>Sport</dt><dd>{`${sportLabel(row.sportType)} · Circuit: Sportya`}</dd>
      </dl>
      <span className="ep-meta">Changing these returns the proposal to Proposed and takes it out of this publish: use Edit proposal.</span>
    </div>

    <div role="group" aria-label="Phases" className="ep-stack">
      <h3>Phases</h3>
      {row.phases.preRegistration && <span>{phaseLine("Pre-registration", row.phases.preRegistration, timeZone)}</span>}
      {row.phases.registrations && <span>{phaseLine("Registrations", row.phases.registrations, timeZone)}</span>}
      {row.passed.map(phase => <span key={phase} className="ep-error-text">{`${PASSED_PHASES[phase].charAt(0).toUpperCase()}${PASSED_PHASES[phase].slice(1)}.`}</span>)}
    </div>

    <div role="group" aria-label="Event" className="ep-stack">
      <h3>Event</h3>
      {field("name", text("name"))}
      {field("season", <span>{row.values.season === "summer" ? "Summer" : row.values.season === "winter" ? "Winter" : "—"}</span>, "From the start date.", true)}
      {field("courtType", select("courtType", settings.courtSurfaces))}
      {field("numberOfCourts", text("numberOfCourts", "number"), row.information?.previousNumberOfCourts != null ? `Previous edition: ${row.information.previousNumberOfCourts}` : null)}
      {field("officialBall", select("officialBall", balls))}
      {field("coordinator", select("coordinator", coordinators))}
      {field("directorName", text("directorName"))}
      {field("refereeName", text("refereeName"))}
      {field("supervisorName", text("supervisorName"))}
      {field("singlesMaxNumber", select("singlesMaxNumber", entries))}
      {field("doublesMaxNumber", select("doublesMaxNumber", entries))}
    </div>

    <div role="group" aria-label="Competitions" className="ep-stack">
      <h3>{`Competitions (${row.competitions.length})`}</h3>
      {row.competitions.map(competition => <div key={competition.id} role="group" aria-label={`Competition ${competitionTile(competition)}`}
        className="ep-competition">
        <div className="ep-competition-fields">
          {competitionControl(competition, "level", settings.levels)}
          {competitionControl(competition, "gameType", settings.gameTypes)}
          {competitionControl(competition, "drawModel", settings.drawModels?.length ? settings.drawModels : DRAW_MODELS)}
          {competition.drawModel === "groups" && competitionControl(competition, "numberOfGroups")}
          {competitionControl(competition, "mlMaxNumber")}
        </div>
      </div>)}
      <span className="ep-meta">Every competition starts with the event; WildCards take the system default.</span>
      <span className="ep-meta">Fees are set on the event after publishing.</span>
    </div>

    <div role="group" aria-label="Logistics sheet" className="ep-stack">
      <h3>Logistics sheet</h3>
      {row.logistics.map(line => {
        const path = `logistics.suppliers.${line.family}`
        const label = `${familyName(line.family)} supplier`
        const product = row.problems.find(problem => problem.field === `logistics.products.${line.family}`)
        return <ReviewField key={line.family} id={id(path)} label={label} source={sourceOf(path)} flag={flagOf([problemOf(path), product].filter(Boolean))} plain={!line.product}
          info={[line.product ? line.product.name || "Product chosen for this proposal" : "No catalogue product", product && problemLabel(product), problemOf(path) && problemLabel(problemOf(path))].filter(Boolean).join(" · ")}>
          {line.product ? <select id={id(path)} className="form-control form-control-sm" value={line.supplier} disabled={locked}
            onChange={event => onSave({ logistics: { suppliers: { ...record.proposal.logistics?.suppliers, [line.family]: event.target.value },
              products: { ...record.proposal.logistics?.products } } }, label)}>
            {Object.entries(supplierNames).map(([value, name]) => <option key={value} value={value}>{name}</option>)}</select>
            : <span className="ep-stack"><b>{supplierNames[line.supplier] || line.supplier}</b><span className="ep-meta">MPG needs a catalogue product</span></span>}
        </ReviewField>
      })}
    </div>

    <div role="group" aria-label="Texts" className="ep-stack ep-gap">
      <h3>Texts</h3>
      {["description", "playerPackage"].map(name => <ReviewText key={name} id={id(name)} name={name} value={row.values[name]} country={row.country}
        source={sourceOf(name)} disabled={locked} onSave={draft => onSave({ [name]: draft }, FIELD_NAMES[name])} />)}
      <span className="ep-meta">The other languages are translated automatically once the event is published.</span>
    </div>
  </section>
}

// While the review is read.
export const ReviewLoading = () => <div className="ep-loading"><Spinner size="sm" /> Loading the review…</div>
